import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    Component,
    computed,
    effect,
    EventEmitter,
    input,
    OnDestroy,
    Output,
    signal,
} from '@angular/core';
import { EntityId } from '@ngrx/signals/entities';
import { AuiButtonComponent } from '@ral/ui:aui/button';
import { AuiIconComponent } from '@ral/ui:aui/icons';

@Component({
    selector: 'aui-toast-item',
    imports: [CommonModule, AuiButtonComponent, AuiIconComponent],
    animations: [
        trigger('toastState', [
            state(
                'visible',
                style({
                    opacity: 1,
                })
            ),
            state(
                'hidden',
                style({
                    opacity: 0,
                })
            ),
            transition('hidden => visible', [
                style({
                    opacity: 0,
                }),
                animate(200),
            ]),
            transition('visible => hidden', [
                animate(
                    200,
                    style({
                        opacity: 0,
                    })
                ),
            ]),
        ]),
    ],
    template: `<div [@toastState]="visible() ? 'visible' : 'hidden'" [class]="toastClasses()">
        <div class="flex h-full gap-2" [ngClass]="{ hidden: hidden() }">
            <aui-icon [icon]="icon()"></aui-icon>
            <div class="h-full w-full max-w-[270px]">
                <div class="flex min-w-0 flex-col gap-2 break-words pt-[1px]">
                    <h3 data-testid="aui-toast-title" class="aui-font-medium-16 m-0">{{ title() }}</h3>
                    <p class="aui-font-regular-14">{{ message() }}</p>
                </div>
            </div>
            <div>
                <aui-button
                    [color]="buttonColor()"
                    (click)="close()"
                    [size]="'24'"
                    icon="times"
                    [buttonStyle]="'solid'"
                    data-testid="aui-toast-title-close"
                ></aui-button>
            </div>
        </div>
    </div>`,
})
export class AuiToastItemComponent implements AfterViewInit, OnDestroy {
    visible = signal(false);
    id = input<EntityId>('');
    title = input<string>('');
    message = input<string>('');
    type = input<'warning' | 'info' | 'success' | 'error'>('info');
    duration = input<number>(0);
    stacked = input<boolean>(true);
    fadeDuration = 200;
    hidden = input<boolean>(false);

    stackedEffect = effect(() => {
        const stacked = this.stacked();
        stacked ? this.initTimeout() : this.clearTimeout();
    });

    @Output() closed: EventEmitter<EntityId> = new EventEmitter<EntityId>();
    timeout: number | null = null;
    toastClasses = computed(() => {
        const tailwindClasses = [
            'rounded',
            'border',
            'p-2',
            'min-h-[90px]',
            'max-w-[340px]',
            'w-full',
            'text-primary-text',
        ];
        const type = this.type();
        if (type === 'warning') {
            tailwindClasses.push('bg-warning-light border-warning');
        } else if (type === 'info') {
            tailwindClasses.push('bg-info-light border-info');
        } else if (type === 'success') {
            tailwindClasses.push('bg-success-light border-success');
        } else if (type === 'error') {
            tailwindClasses.push('bg-danger-light border-danger');
        }

        return tailwindClasses.join(' ');
    });

    close() {
        this.closed.emit(this.id());
    }

    ngAfterViewInit(): void {
        this.visible.set(true);
    }

    initTimeout() {
        this.timeout = Number(
            setTimeout(() => {
                this.visible.set(false);
                this.timeout = Number(
                    setTimeout(() => {
                        this.closed.emit(this.id());
                    }, this.fadeDuration)
                );
            }, this.duration() - this.fadeDuration)
        );
    }

    clearTimeout() {
        if (this.timeout) {
            clearTimeout(this.timeout);
            this.timeout = null;
        }
    }

    ngOnDestroy() {
        this.clearTimeout();
    }

    icon = computed(() => {
        const type = this.type();
        if (type === 'warning') {
            return 'triangle-exclamation';
        } else if (type === 'info') {
            return 'circle-info';
        } else if (type === 'success') {
            return 'check';
        } else if (type === 'error') {
            return 'circle-exclamation';
        }
        return 'circle-info';
    });

    buttonColor = computed(() => {
        const type = this.type();
        if (type === 'warning') {
            return 'warning';
        }
        if (type === 'info') {
            return 'info';
        }
        if (type === 'success') {
            return 'success';
        }
        if (type === 'error') {
            return 'danger';
        }
        return 'gray';
    });
}
